/**
 * The comparison semantics of ONE stored pattern-rule condition.
 *
 * `bat.exchange_api_templates.pattern_rules` is read by THREE engines:
 *   - `PatternMatchingService` — the engine that classifies a real sync;
 *   - `ApiPatternMatcherService` — the super-admin server preview ("Simular");
 *   - `clientSideMatcher` — the in-browser live preview the admin watches
 *     while authoring a rule (coverage widget + "Produces transaction").
 *
 * The three used to carry SEPARATE copies of these comparisons, and the copies
 * drifted in exactly three ways: strict `===` versus stringified equality,
 * `Number()` versus `parseFloat`, and an unguarded `RegExp` versus a
 * ReDoS-screened one. #1074 unified the two BACKEND engines here; the browser
 * engine was left behind and still carried all three drifts, so the editor
 * could report "no sample row matches" for a rule that the import matches.
 *
 * This module is the single implementation all three now call, so a future
 * change cannot move one engine without the others. The semantics are the SYNC
 * engine's, verbatim — that engine classifies real user data and also serves
 * user-authored CSV mappings in `bat.import_mappings`, so its behaviour is the
 * contract and every preview is what has to move (#1073's direction).
 *
 * It lives in `@bat/shared` rather than in the backend because the browser is
 * one of the three consumers and must not fork it again. No runtime deps.
 */

import { screenRegexPattern } from './regex-pattern-screen.js'

/**
 * Everything is compared as a string, and an absent field reads as `''`.
 *
 * That is deliberate, not sloppiness: a stored rule's `value` arrives from JSONB
 * and an exchange record's field may be a string, a number or a boolean for the
 * SAME logical field across exchanges (Bybit quotes amounts as strings, Binance
 * as numbers). Strict identity would make `status: 1` fail a stored `value: '1'`.
 */
export function compareStringValues(
  actual: unknown,
  expected: unknown,
  operator: 'equals' | 'contains' | 'starts_with' | 'ends_with',
  caseSensitive = false,
): boolean {
  const actualString = String(actual ?? '')
  const expectedString = String(expected ?? '')
  const left = caseSensitive ? actualString : actualString.toLowerCase()
  const right = caseSensitive ? expectedString : expectedString.toLowerCase()

  switch (operator) {
    case 'equals':
      return left === right
    case 'contains':
      return left.includes(right)
    case 'starts_with':
      return left.startsWith(right)
    case 'ends_with':
      return left.endsWith(right)
    default:
      return false
  }
}

/**
 * Numeric comparison over values that reach us as strings with units or
 * thousands marks ('12 BTC', '1,234.5'). Non-numeric characters are stripped
 * before parsing, and an unparseable operand answers `false` on BOTH sides —
 * never `NaN`-propagates into a comparison that would silently read as `>`.
 */
export function compareNumericValues(
  actual: unknown,
  expected: unknown,
  operator: 'gt' | 'lt' | 'gte' | 'lte',
): boolean {
  const left = parseFloat(String(actual ?? '').replace(/[^0-9.-]/g, ''))
  const right = parseFloat(String(expected ?? '').replace(/[^0-9.-]/g, ''))

  if (isNaN(left) || isNaN(right)) {
    return false
  }

  switch (operator) {
    case 'gt':
      return left > right
    case 'lt':
      return left < right
    case 'gte':
      return left >= right
    case 'lte':
      return left <= right
    default:
      return false
  }
}

/**
 * Regex matching with the ReDoS screen the import engine applies. It THROWS on
 * a rejected or invalid pattern rather than answering `false`, because a pattern
 * that cannot be evaluated is not the same as a pattern that did not match — and
 * an import that silently classified nothing would be worse than one that stops.
 *
 * The server preview catches this and reports it as a failed condition, so an
 * admin sees the reason instead of a bare "no match" for a rule that would take
 * the whole import down.
 */
export function matchesRegexPattern(value: string, pattern: string): boolean {
  // The screen is a real tokenizer (see `regex-pattern-screen.ts`). The regexes
  // it replaced refused every pattern holding `+`, `*` or a literal `1`.
  const screen = screenRegexPattern(pattern)
  if (!screen.accepted) {
    throw new Error(screen.message)
  }
  return new RegExp(pattern).test(value)
}
